import React from 'react';
import { Link } from 'react-router-dom';

function TermsOfService() {
  return (
    <div className="public-page">
      {/* Navigation - Premium Compact Header */}
      <nav className="public-nav">
        <div className="public-nav-container">
          <Link to="/" className="public-nav-brand">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="nav-brand-logo" 
            />
          </Link>
          <div className="public-nav-links">
            <Link to="/" className="public-nav-link">Home</Link>
            <Link to="/pricing" className="public-nav-link">Pricing</Link>
            <Link to="/login" className="public-nav-link">Log in</Link>
          </div>
        </div>
      </nav>

      {/* Terms of Service Content */}
      <div className="methodology-page" style={{ paddingTop: '2rem' }}>
        <div className="methodology-container">
          <div className="methodology-hero">
            <p className="methodology-kicker">Legal</p>
            <h1 className="methodology-title">Terms of Service</h1>
            <p className="methodology-subtitle">
              Terms and conditions for using Black Peak Analytics
            </p>
          </div>

          {/* Last Updated */}
          <div style={{ 
            marginBottom: '2rem', 
            padding: '1rem', 
            backgroundColor: 'rgba(16, 185, 129, 0.1)', 
            borderRadius: '0.5rem',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}>
            <p className="theme-text" style={{ margin: 0 }}>
              <strong>Last Updated:</strong> January 2025
            </p>
          </div>

          {/* Acceptance of Terms */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Acceptance of Terms</h2>
            <p>
              By accessing or using Black Peak Analytics ("the Service," "we," "our," or "us"), you agree to be 
              bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
            </p>
            <p>
              These terms apply to all users, including those on Free Preview, Pro, and Premium subscription plans.
            </p>
          </div>

          {/* What We Provide */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">What Black Peak Analytics Provides</h2>
            <p>
              Black Peak Analytics is a stock research and portfolio management platform that provides:
            </p>
            <ul className="methodology-list">
              <li>Stock screening and ranking tools based on our proprietary Investment Score model</li>
              <li>Historical backtesting and validation data for model performance analysis</li>
              <li>Portfolio tracking, alerts, and comparison tools</li>
              <li>Fundamental data, price charts, and research reports</li>
              <li>PDF export capabilities for scans and portfolio analysis</li>
            </ul>
            <p>
              The specific features available to you depend on your subscription plan (Free Preview, Pro, or Premium).
            </p>
          </div>

          {/* Research Tool Only */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Research Tool Only - Not Investment Advice</h2>
            <p>
              <strong>Black Peak Analytics is a research and analysis tool only.</strong> We are not a financial 
              advisor, broker, investment adviser, or registered investment company.
            </p>
            <p>
              All Investment Scores, rankings, scans, alerts, reports, backtesting results, and other content 
              provided through the Service are for informational and educational purposes only. Nothing on this 
              platform constitutes personalized investment advice or a recommendation to buy, sell, or hold any security.
            </p>
            <p>
              You are solely responsible for your own investment decisions. Before making any investment, you should 
              conduct your own research and consult with qualified financial, legal, and tax professionals as 
              appropriate for your individual circumstances.
            </p>
            <p>
              For complete details, please review our{' '}
              <Link 
                to="/investment-disclaimer" 
                className="theme-link"
                style={{ fontWeight: '500' }}
              >
                Investment Disclaimer
              </Link>.
            </p>
          </div>

          {/* User Accounts */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">User Accounts and Responsibility</h2>
            <p>
              To use Black Peak Analytics, you must create an account with a valid email address and password. 
              You are responsible for:
            </p>
            <ul className="methodology-list">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
              <li>Ensuring that your account information is accurate and up to date</li>
            </ul>
            <p>
              You must be at least 18 years old to use Black Peak Analytics. By creating an account, you represent 
              that you meet this age requirement.
            </p>
          </div>

          {/* Subscription Plans and Billing */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Subscription Plans and Billing</h2>
            
            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Available Plans</h3>
              <p>
                Black Peak Analytics offers three subscription tiers:
              </p>
              <ul className="methodology-list">
                <li><strong>Free Preview:</strong> Limited access to core features with usage restrictions</li>
                <li><strong>Pro:</strong> Full access to screening, alerts, and portfolio management features</li>
                <li><strong>Premium:</strong> All Pro features plus Strategy Lab, written interpretations,
                and advanced analysis tools</li>
              </ul>
              <p>
                Current pricing and feature details are available on our{' '}
                <Link 
                  to="/pricing" 
                  className="theme-link"
                  style={{ fontWeight: '500' }}
                >
                  Pricing page
                </Link>.
              </p>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Billing and Payment</h3>
              <p>
                Paid subscriptions (Pro and Premium) are billed on a recurring basis—either monthly or annually, 
                depending on the plan you select. Payment processing is handled securely by Stripe, our third-party 
                payment processor.
              </p>
              <p>
                By subscribing to a paid plan, you authorize us to charge your payment method on a recurring basis 
                until you cancel your subscription. Subscription fees are non-refundable except as required by law.
              </p>
              <p>
                We reserve the right to change our pricing at any time. If we increase the price of your subscription, 
                we will notify you in advance, and the new price will take effect at your next billing cycle.
              </p>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Free Account</h3>
              <p>
                We offer a Free account with limited access to core features so users can try the platform before choosing a paid plan. The Free plan does not require a paid subscription. Paid Pro and Premium features are only activated when you choose a paid plan and complete checkout. 
              </p>
            </div>
          </div>

          {/* Cancellations and Downgrades */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Cancellations and Downgrades</h2>
            
            <div className="methodology-component-card">
              <h3 className="methodology-component-title">How to Cancel</h3>
              <p>
                You can cancel your paid subscription at any time from the Billing page in your account dashboard. 
                Click the "Cancel Subscription" button and follow the prompts to complete the cancellation through 
                the Stripe Customer Portal.
              </p>
              <p>
                When you cancel a paid subscription:
              </p>
              <ul className="methodology-list">
                <li>Your paid access will continue until the end of your current billing period</li>
                <li>You will not be charged again after the current period ends</li>
                <li>Your account will automatically downgrade to the Free Preview plan when the paid period expires</li>
                <li>You will retain access to all saved scans, portfolios, and holdings</li>
              </ul>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">After Downgrade to Free Preview</h3>
              <p>
                When your account downgrades to Free Preview (either by cancellation or failed payment), your data 
                is preserved, but Free Preview plan limits apply to future actions:
              </p>
              <ul className="methodology-list">
                <li>You can view all previously saved scans and portfolios</li>
                <li>You can view all previously added portfolio holdings</li>
                <li>You cannot create new portfolios beyond the Free Preview limit (1 portfolio)</li>
                <li>You cannot access Pro-only or Premium-only features such as alerts, backtesting, or written interpretations</li>
                <li>New scans and price charts are limited to Free Preview restrictions</li>
              </ul>
              <p>
                You can upgrade back to a paid plan at any time to regain full access to all features.
              </p>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">No Refunds</h3>
              <p>
                Subscription fees are non-refundable. If you cancel your subscription, you will not receive a refund 
                for the current billing period, but you will retain access to paid features until the period ends.
              </p>
            </div>
          </div>

          {/* User Data */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Saved Scans, Portfolios, and User Data</h2>
            <p>
              When you use Black Peak Analytics, you can save scans, create portfolios, add holdings, and set alert 
              preferences. This data is stored in your account and is accessible to you as long as your account remains active.
            </p>
            <p>
              You retain ownership of the data you input into the Service (such as portfolio holdings and ticker selections). 
              We do not claim ownership of your investment decisions or portfolio compositions.
            </p>
            <p>
              We reserve the right to delete inactive accounts and associated data after a reasonable period of inactivity. 
              If you wish to delete your account and all associated data, please contact our support team.
            </p>
            <p>
              For details on how we collect, use, and protect your data, please review our{' '}
              <Link 
                to="/privacy" 
                className="theme-link"
                style={{ fontWeight: '500' }}
              >
                Privacy Policy
              </Link>.
            </p>
          </div>

          {/* Acceptable Use */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Acceptable Use</h2>
            <p>
              You agree to use Black Peak Analytics only for lawful purposes and in accordance with these Terms. 
              You agree not to:
            </p>
            <ul className="methodology-list">
              <li>Use the Service in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the Service, other user accounts, or our systems</li>
              <li>Use automated tools (bots, scrapers, etc.) to access or extract data from the Service without our 
              written permission</li>
              <li>Reverse engineer, decompile, or attempt to derive the source code of our proprietary Investment 
              Score model or algorithms</li>
              <li>Resell, redistribute, or commercially exploit the Service or its content without our written permission</li>
              <li>Use the Service to transmit viruses, malware, or other harmful code</li>
              <li>Impersonate another person or entity, or falsely represent your affiliation with any person or entity</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate your account if we determine, in our sole discretion, 
              that you have violated these acceptable use terms.
            </p>
          </div>

          {/* Data Accuracy and Model Limitations */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Data Accuracy and Model Limitations</h2>
            <p>
              Black Peak Analytics relies on third-party data providers for stock prices, fundamentals, and market data. 
              While we strive to use reliable sources, we cannot guarantee the accuracy, completeness, or timeliness 
              of this data.
            </p>
            <p>
              Our Investment Score model and ranking algorithms are based on quantitative analysis and historical data. 
              Model outputs should be considered as one of many factors in your investment research process, not as 
              the sole basis for investment decisions.
            </p>
            <p>
              Historical backtesting results do not guarantee future performance. Past performance is not indicative 
              of future results. Market conditions, company fundamentals, and countless other variables can change 
              rapidly and unpredictably.
            </p>
            <p>
              <strong>We make no guarantees, representations, or warranties regarding the accuracy, completeness, or 
              reliability of any information, data, scores, rankings, or analysis provided through the Service.</strong>
            </p>
          </div>

          {/* Third-Party Services */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Third-Party Services</h2>
            
            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Stripe Payment Processing</h3>
              <p>
                All subscription payments are processed by Stripe, a third-party payment processor. When you subscribe 
                to a paid plan, you will be redirected to Stripe's secure checkout page. Your payment information is 
                handled directly by Stripe and is not stored on our servers.
              </p>
              <p>
                Stripe's use of your payment information is governed by their Privacy Policy and Terms of Service. 
                We are not responsible for Stripe's handling of your payment data.
              </p>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Market Data Providers</h3>
              <p>
                We use third-party financial data providers to obtain stock prices, fundamentals, and market data. 
                These providers do not receive your personal information or know which stocks you're researching.
              </p>
              <p>
                We are not responsible for the accuracy, completeness, or availability of data provided by these 
                third-party sources.
              </p>
            </div>
          </div>

          {/* Service Availability */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Service Availability</h2>
            <p>
              We strive to provide reliable and uninterrupted access to Black Peak Analytics. However, we do not 
              guarantee that the Service will be available at all times or that it will be error-free.
            </p>
            <p>
              The Service may be temporarily unavailable due to:
            </p>
            <ul className="methodology-list">
              <li>Scheduled maintenance or updates</li>
              <li>Technical issues or server outages</li>
              <li>Third-party service disruptions (e.g., data provider outages)</li>
              <li>Events beyond our reasonable control</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or 
              without notice. We will not be liable for any modification, suspension, or discontinuation of the Service.
            </p>
          </div>

          {/* Limitation of Liability */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Limitation of Liability</h2>
            <p>
              <strong>To the fullest extent permitted by law, Black Peak Analytics and its operators, employees, and 
              affiliates shall not be liable for any direct, indirect, incidental, consequential, or punitive damages 
              arising from your use of the Service, including but not limited to:</strong>
            </p>
            <ul className="methodology-list">
              <li>Investment losses or financial damages resulting from decisions made based on information provided 
              through the Service</li>
              <li>Errors, inaccuracies, or omissions in data, scores, rankings, or analysis</li>
              <li>Service interruptions, downtime, or technical issues</li>
              <li>Unauthorized access to your account or data</li>
              <li>Third-party actions or failures (e.g., payment processor issues, data provider outages)</li>
            </ul>
            <p>
              <strong>In no event shall our total liability to you for all claims arising from or related to the 
              Service exceed the amount you paid us in subscription fees during the twelve (12) months preceding 
              the claim.</strong>
            </p>
            <p>
              Some jurisdictions do not allow the exclusion or limitation of certain damages, so some of the above 
              limitations may not apply to you.
            </p>
          </div>

          {/* Intellectual Property */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Intellectual Property</h2>
            <p>
              Black Peak Analytics, including our Investment Score model, algorithms, software, design, content, and 
              branding, is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              You are granted a limited, non-exclusive, non-transferable license to access and use the Service for 
              your personal, non-commercial use in accordance with these Terms. You may not copy, modify, distribute, 
              sell, or lease any part of the Service without our written permission.
            </p>
          </div>

          {/* Changes to Terms */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Changes to These Terms</h2>
            <p>
              We may update these Terms of Service from time to time to reflect changes in our practices, legal 
              requirements, or for other operational reasons. We will notify you of any material changes by posting 
              the updated Terms on this page and updating the "Last Updated" date.
            </p>
            <p>
              Your continued use of Black Peak Analytics after any changes indicates your acceptance of the updated 
              Terms. If you do not agree with the updated Terms, you should discontinue using the Service and cancel 
              your subscription.
            </p>
          </div>

          {/* Governing Law */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Governing Law</h2>
            <p>
            These Terms are governed by the laws of the Province of Alberta and the federal laws of Canada applicable in Alberta. Nothing in these Terms limits any consumer rights that cannot be waived under applicable law.
            </p>
         </div>

          {/* Contact Information */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Contact and Support</h2>
            <p>
              If you have questions, concerns, or requests regarding these Terms of Service, please contact us:
            </p>
            <div style={{ 
              marginTop: '1.5rem',
              padding: '1.5rem',
              backgroundColor: 'rgba(8, 22, 38, 0.5)',
              border: '1px solid rgba(71, 85, 105, 0.4)',
              borderRadius: '0.5rem'
            }}>
              <p style={{ margin: '0 0 1rem 0' }}>
                <strong>Email:</strong>{' '}
                <a 
                  href="mailto:support@blackpeakanalytics.com"
                  className="theme-link"
                >
                  support@blackpeakanalytics.com
                </a>
              </p>
              <p className="theme-text" style={{ margin: 0 }}>
                We will respond to your inquiry within 2-3 business days.
              </p>
            </div>
            <p style={{ marginTop: '1.5rem' }}>
              For additional help and frequently asked questions, visit our{' '}
              <Link 
                to="/support" 
                className="theme-link"
                style={{ fontWeight: '500' }}
              >
                Support page
              </Link>.
            </p>
          </div>

          {/* Severability */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable by a court of law, the remaining 
              provisions shall continue in full force and effect. The invalid or unenforceable provision shall be 
              replaced with a valid provision that most closely reflects the intent of the original provision.
            </p>
          </div>

          {/* Entire Agreement */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Entire Agreement</h2>
            <p>
              These Terms of Service, together with our Privacy Policy and Investment Disclaimer, constitute the 
              entire agreement between you and Black Peak Analytics regarding your use of the Service. These Terms 
              supersede any prior agreements or understandings, whether written or oral.
            </p>
          </div>
    
        </div>
      </div>

      {/* Footer */}
      <footer className="public-footer public-footer-compact">
        <div className="public-footer-container public-footer-container-compact">
          <Link to="/" className="footer-brand-compact">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="footer-brand-logo" 
            />
          </Link>
          <nav className="footer-nav-compact" aria-label="Footer navigation">
            <Link to="/" className="public-footer-link">Home</Link>
            <Link to="/pricing" className="public-footer-link">Pricing</Link>
            <Link to="/support" className="public-footer-link">Support</Link>
            <Link to="/privacy" className="public-footer-link">Privacy</Link>
            <Link to="/terms" className="public-footer-link">Terms</Link>
            <Link to="/investment-disclaimer" className="public-footer-link">Disclaimer</Link>
          </nav>
          <p className="footer-disclaimer footer-disclaimer-compact">Independent research tool. Not financial advice.</p>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
